import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { LifeBuoy, LayoutDashboard, Ticket, Users, LogOut, School, Users2, ShieldCheck, Eye, X, ScrollText, Settings2, Palette, KeyRound, AlertTriangle, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITES, SITE_LABEL, SITE_SHORT, setSiteTagMap, setSiteCrests, siteCrest, siteTagClass, subscribeSiteCrests, subscribeSiteTags } from "@/lib/sites";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import HelpKb from "@/components/HelpKb";

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
        isActive
          ? "bg-zinc-100 text-zinc-950 dark:bg-white/10 dark:text-white"
          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100"
      }`
    }
  >
    <Icon className="h-4 w-4" strokeWidth={2} />
    <span>{label}</span>
  </NavLink>
);

const ROLE_LABEL = {
  user: "User",
  teacher: "Teacher",
  secretary: "Secretary",
  principal: "Principal",
  it: "IT",
  maintenance: "Maintenance",
  custodian: "Custodian",
  mechanic: "Mechanic",
  admin: "Master Admin",
};

export default function AppShell() {
  const { user, logout, stopImpersonation } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [pwOpen, setPwOpen] = useState(false);
  // Mobile drawer state — only relevant on <lg screens (desktop keeps a static sidebar)
  const [mobileOpen, setMobileOpen] = useState(false);
  // Close the drawer whenever the route changes so tapping a nav item feels natural
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // SSO-provisioned users don't have a local password to rotate — hide the option
  const canChangePassword = user && !user.sso_provisioned;

  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const isRequester = user?.role === "user" || user?.role === "teacher";
  const canDashboard = !isRequester; // secretary, principal, staff, admin
  const isAdmin = user?.role === "admin";
  const canRoster = user?.role === "principal" || user?.role === "secretary" || isAdmin;
  // Admins oversee the whole district — surface the full site roster in the sidebar.
  // Everyone else sees only their assigned sites (fallback to legacy single-site field).
  const sites = isAdmin
    ? SITES.map((s) => s.code)
    : (user?.sites || (user?.site ? [user.site] : []));

  useEffect(() => {
    if (!user) return;
    api.get("/settings/site-tags").then((r) => setSiteTagMap(r.data)).catch(() => {});
    api.get("/settings/dept-tags").then((r) => import("@/lib/sites").then(m => m.setDeptTagMap(r.data))).catch(() => {});
    api.get("/settings/status-tags").then((r) => import("@/lib/sites").then(m => m.setStatusTagMap(r.data))).catch(() => {});
    api.get("/settings/priority-tags").then((r) => import("@/lib/sites").then(m => m.setPriorityTagMap(r.data))).catch(() => {});
    api.get("/settings/site-crests").then((r) => setSiteCrests(r.data)).catch(() => {});
    api.get("/settings/login-logo").then((r) => setDistrictLogo(r.data?.data_url || null)).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Maintenance-mode banner state — polled + reactive to Settings-page toggles
  const [maintenance, setMaintenance] = useState({ enabled: false, reason: "" });
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchStatus = () => {
      api.get("/system/maintenance-status")
        .then((r) => { if (!cancelled) setMaintenance(r.data || { enabled: false, reason: "" }); })
        .catch(() => {});
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 60_000);           // refresh every minute
    const onChange = (e) => setMaintenance(e.detail || { enabled: false, reason: "" });
    window.addEventListener("maintenance-mode-changed", onChange);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener("maintenance-mode-changed", onChange); };
  }, [user?.id]);

  const [, setCrestTick] = useState(0);
  useEffect(() => subscribeSiteCrests(() => setCrestTick((n) => n + 1)), []);
  useEffect(() => subscribeSiteTags(() => setCrestTick((n) => n + 1)), []);
  const [districtLogo, setDistrictLogo] = useState(null);

  return (
    <div className="min-h-screen flex flex-col bg-zinc-100" data-testid="app-shell">
      {maintenance.enabled && (
        <div className="bg-red-600 text-white border-b border-red-800" data-testid="maintenance-banner">
          <div className="px-4 md:px-6 py-2 flex items-center gap-3">
            <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={2.4} />
            <div className="text-sm min-w-0 flex-1 truncate">
              <strong className="uppercase tracking-[0.08em] text-[11px]">Maintenance mode</strong>
              <span className="mx-2 opacity-70">·</span>
              <span>
                {maintenance.reason ? maintenance.reason : "System under maintenance — email notifications are paused."}
              </span>
            </div>
          </div>
        </div>
      )}
      {user?.impersonating && (
        <div className="bg-amber-400 text-zinc-950 border-b border-amber-500" data-testid="impersonation-banner">
          <div className="px-4 md:px-6 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm min-w-0 flex-1">
              <Eye className="h-4 w-4 shrink-0" strokeWidth={2.2} />
              <div className="truncate min-w-0">
                <strong>{user.impersonated_by?.name || "Master admin"}</strong>
                <span className="mx-1">is viewing as</span>
                <strong>{user.name}</strong>
                <span className="ml-2 text-xs text-zinc-800/70">
                  · {user.role}{user.sites?.length ? ` · ${user.sites.map((c)=>SITE_SHORT[c]||c).join(" · ")}` : ""}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={async () => { await stopImpersonation(); toast.success("Impersonation ended"); window.location.href = "/admin/users"; }}
              data-testid="stop-impersonation-button"
              className="inline-flex items-center gap-1.5 text-xs font-medium bg-zinc-950 text-white hover:bg-zinc-800 transition-colors duration-200 px-3 py-1 rounded-md shrink-0"
            >
              <X className="h-3 w-3" /> End impersonation
            </button>
          </div>
        </div>
      )}
      {/* Mobile-only top bar with hamburger — hidden on lg+ where the static sidebar is visible */}
      <div className="lg:hidden flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800" data-testid="mobile-topbar">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-toggle"
          aria-label="Open navigation menu"
          className="p-2 -ml-2 rounded-md text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        >
          <Menu className="h-5 w-5" strokeWidth={2.2} />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          {districtLogo ? (
            <img src={districtLogo} alt="District logo" className="max-h-8 max-w-[140px] object-contain" />
          ) : (
            <>
              <div className="h-7 w-7 rounded-md bg-zinc-950 text-white dark:bg-white dark:text-zinc-950 grid place-items-center shrink-0">
                <LifeBuoy className="h-3.5 w-3.5" strokeWidth={2.2} />
              </div>
              <div className="font-heading font-bold text-sm text-zinc-950 dark:text-white truncate">HelpDesk · ESUSD</div>
            </>
          )}
        </div>
        {/* Balance spacer so the logo stays visually centered */}
        <div className="w-9 shrink-0" aria-hidden />
      </div>

      <div className="flex flex-1 min-h-0 relative">
      {/* Mobile drawer backdrop — click to close */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40 transition-opacity"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-drawer-backdrop"
          aria-hidden
        />
      )}
      <aside
        data-testid="app-sidebar"
        className={`w-64 shrink-0 bg-white text-zinc-900 border-r border-zinc-200 dark:bg-zinc-950 dark:text-zinc-100 dark:border-transparent flex flex-col
          fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-out
          lg:static lg:translate-x-0 lg:transform-none
          ${mobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}`}
      >
        {/* Close button — only visible on mobile when the drawer is open */}
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-drawer-close"
          aria-label="Close navigation"
          className="lg:hidden absolute top-3 right-3 p-2 rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-white/10 transition-colors z-10"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="px-6 py-6 border-b border-zinc-200 dark:border-white/10">
          {districtLogo ? (
            <div className="flex flex-col items-start gap-1.5" data-testid="sidebar-district-logo">
              <img
                src={districtLogo}
                alt="District logo"
                className="max-h-12 max-w-full object-contain drop-shadow-[0_2px_6px_rgba(255,255,255,0.15)]"
              />
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 dark:text-zinc-500 pl-0.5">HelpDesk · ESUSD</div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-zinc-950 text-white dark:bg-white dark:text-zinc-950 grid place-items-center">
                <LifeBuoy className="h-4 w-4" strokeWidth={2.2} />
              </div>
              <div>
                <div className="font-heading font-bold text-base tracking-tight leading-tight">HelpDesk</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">ESUSD</div>
              </div>
            </div>
          )}
          {sites.length > 0 && (
            <div className="mt-4 space-y-1.5">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 dark:text-zinc-400 flex items-center gap-1.5">
                <School className="h-3 w-3" /> {isAdmin ? `All sites · ${sites.length}` : (sites.length === 1 ? "Site" : `${sites.length} sites`)}
              </div>
              <div className="flex flex-wrap gap-1" data-testid="sidebar-user-sites">
                {sites.map((code) => (
                  <div key={code} className="inline-flex items-center gap-1" title={SITE_LABEL[code] || code}>
                    {siteCrest(code) && (
                      <img
                        src={siteCrest(code)}
                        alt=""
                        className="h-3.5 w-3.5 rounded-sm object-contain shrink-0"
                        data-testid={`sidebar-crest-${code}`}
                      />
                    )}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono border ${siteTagClass(code)}`}>
                      {SITE_SHORT[code] || code}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {isAdmin && (
            <div className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-400/20 dark:text-amber-300 dark:border-amber-400/30 px-2 py-0.5 rounded-full" data-testid="master-admin-badge">
              <ShieldCheck className="h-3 w-3" /> Master admin
            </div>
          )}
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-3 mb-2">Workspace</div>
          {canDashboard && <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />}
          {canRoster && <NavItem to="/roster" icon={Users2} label="Roster" testId="nav-roster" />}
          <NavItem to="/tickets" icon={Ticket} label={canDashboard ? "My tickets" : "Tickets"} testId="nav-tickets" />
          {isAdmin && (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-3 pt-4 mb-2">Admin</div>
              <NavItem to="/admin/users" icon={Users} label="Users" testId="nav-users" />
              <NavItem to="/admin/audit" icon={ScrollText} label="Audit log" testId="nav-audit" />
              <NavItem to="/admin/settings" icon={Settings2} label="Settings" testId="nav-settings" />
              <NavItem to="/admin/tags" icon={Palette} label="Site tags" testId="nav-tags" />
            </>
          )}
        </nav>

        <div className="p-3 border-t border-zinc-200 dark:border-white/10">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="h-9 w-9 rounded-full bg-[#0055FF] text-white grid place-items-center text-xs font-semibold">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-[11px] text-zinc-500 truncate">
                {ROLE_LABEL[user?.role] || user?.role}{user?.department ? ` · ${user.department}` : ""}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="logout-button"
            className="w-full mt-1 justify-start text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-white dark:hover:bg-white/5"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
          {canChangePassword && (
            <Button
              variant="ghost"
              onClick={() => setPwOpen(true)}
              data-testid="change-password-button"
              className="w-full justify-start text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-white dark:hover:bg-white/5 text-xs"
            >
              <KeyRound className="h-3.5 w-3.5 mr-2" /> Change password
            </Button>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <HelpKb />
    </div>
  );
}
